import { createClient } from '@supabase/supabase-js';
// SINGLETON PATTERN: Ensure only ONE Supabase client instance exists
let _configOverride = null;
let _supabaseClient = null;
let _configurationInitialized = false;
let _clientCreationCount = 0;
// Track client instances for debugging
const logClientCreation = () => {
    _clientCreationCount++;
    console.log(`🔄 Supabase client creation #${_clientCreationCount}`, {
        existingClient: !!_supabaseClient,
        configInitialized: _configurationInitialized,
        configOverride: !!_configOverride
    });
    // Warn if too many clients are being created
    if (_clientCreationCount > 3) {
        console.warn(`⚠️ WARNING: ${_clientCreationCount} Supabase clients created - possible multiple instances issue`);
    }
};
// No early initialization - will check on first client access
export const setSupabaseConfig = (config) => {
    console.log('🔧 setSupabaseConfig called with:', config.url);
    // SINGLETON: Only recreate client if config actually changed
    const configChanged = !_configOverride ||
        _configOverride.url !== config.url ||
        _configOverride.anonKey !== config.anonKey;
    if (!configChanged && _supabaseClient) {
        console.log('✅ Config unchanged and client exists - reusing singleton');
        return;
    }
    _configOverride = config;
    _configurationInitialized = true;
    // Only invalidate client if config actually changed
    if (configChanged) {
        const oldClient = _supabaseClient;
        _supabaseClient = null;
        console.log('🔧 Config changed - Supabase client invalidated (was:', !!oldClient, ') - will be recreated on next access');
    }
};
// Environment-agnostic configuration
export const getSupabaseConfig = () => {
    console.log('🔍 getSupabaseConfig: _configOverride exists?', !!_configOverride);
    if (_configOverride) {
        console.log('🔍 Using override config:', _configOverride.url);
        return _configOverride;
    }
    // BLOCK: Prevent any client creation before explicit configuration
    if (!_configurationInitialized) {
        console.error('🚨 BLOCKING: Supabase client access before configuration initialization!');
        console.error('🚨 This prevents mixed client creation. Configuration must be set first.');
        throw new Error('Supabase client accessed before configuration - call initializeEnvironment() first');
    }
    // If we reach here, configuration was not properly initialized
    console.error('💥 NUCLEAR: Configuration access after initialization but no override set');
    console.error('💥 This should be impossible - _configurationInitialized:', _configurationInitialized);
    console.error('💥 _configOverride:', _configOverride);
    throw new Error('NUCLEAR: Impossible configuration state - no override after initialization');
};
// Create and export the Supabase client with singleton protection
export const createSupabaseClient = () => {
    const config = getSupabaseConfig();
    logClientCreation();
    console.log('🔧 createSupabaseClient: Creating client with config:', { url: config.url, hasAnonKey: !!config.anonKey });
    // SINGLETON: Add unique storage key based on URL to prevent cross-environment conflicts
    const urlHash = config.url.split('.')[0].split('//')[1] || 'default';
    const storageKey = `sb-${urlHash}-auth-token`;
    const client = createClient(config.url, config.anonKey, {
        auth: {
            // Prevent automatic logout on invalid session
            autoRefreshToken: true,
            persistSession: true,
            detectSessionInUrl: true,
            debug: false,
            flowType: 'pkce',
            // SINGLETON: Unique storage key to prevent cross-environment conflicts
            storageKey: storageKey
        },
        global: {
            fetch: typeof window !== 'undefined' ? window.fetch : undefined,
        }
    });
    console.log('🔧 createSupabaseClient: Client created with storageKey:', storageKey);
    console.log('🔧 createSupabaseClient: Client has methods - from:', !!client.from, 'auth:', !!client.auth);
    return client;
};
// Export configuration for use in other functions
export const getEnvironmentAwareSupabaseUrl = () => {
    // Check environment variables first (for Vercel deployments)
    if (typeof process !== 'undefined' && process.env) {
        if (process.env.SUPABASE_URL) {
            return process.env.SUPABASE_URL;
        }
        if (process.env.VITE_SUPABASE_URL) {
            return process.env.VITE_SUPABASE_URL;
        }
    }
    // Fallback to client-side config if available and initialized
    try {
        if (_configurationInitialized && _configOverride) {
            return _configOverride.url;
        }
    }
    catch (error) {
        // Ignore configuration errors when used from service contexts
    }
    // Production fallback when called from service context (browser production deployment)
    if (typeof window !== 'undefined') {
        // We're in browser context, use production URL as fallback
        return 'https://killgsyghirjkdsjnfta.supabase.co';
    }
    // Final fallback - should not happen in production
    throw new Error('Unable to determine Supabase URL for environment');
};
export const getEnvironmentAwareSupabaseServiceKey = () => {
    // Check environment variables first (for Vercel deployments)
    if (typeof process !== 'undefined' && process.env) {
        const supabaseUrl = process.env.SUPABASE_URL || process.env.VITE_SUPABASE_URL;
        // For local development, return the demo service role key
        if (supabaseUrl === 'http://127.0.0.1:54321') {
            return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImV4cCI6MTk4MzgxMjk5Nn0.EGIM96RAZx35lJzdJsyH-qQwv8Hdp7fsn3W0YpN81IU';
        }
        // For staging environment, return the staging service key
        if (supabaseUrl === 'https://rntynqplrfyidfwnifip.supabase.co') {
            return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJudHlucXBscmZ5aWRmd25pZmlwIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc2MTA1NTU3NywiZXhwIjoyMDc2NjMxNTc3fQ.ijQ2sVBK4nOL3IiHhvrwmLuGJBxsmqzXwgQSrUe6jTs';
        }
        // For production environment, return the production service key
        if (supabaseUrl === 'https://killgsyghirjkdsjnfta.supabase.co') {
            return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtpbGxnc3lnaGlyamtkc2puZnRhIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc2MTA2MjA2NCwiZXhwIjoyMDc2NjM4MDY0fQ.Nj1_F_MmG9brvw2YdLZPEbo8qXcxjNgYhB8X-ENndJs';
        }
    }
    // Fallback to client-side config matching if available and initialized
    try {
        if (_configurationInitialized && _configOverride) {
            // For local development, return the demo service role key
            if (_configOverride.url === 'http://127.0.0.1:54321') {
                return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImV4cCI6MTk4MzgxMjk5Nn0.EGIM96RAZx35lJzdJsyH-qQwv8Hdp7fsn3W0YpN81IU';
            }
            // For staging environment, return the staging service key
            if (_configOverride.url === 'https://rntynqplrfyidfwnifip.supabase.co') {
                return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJudHlucXBscmZ5aWRmd25pZmlwIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc2MTA1NTU3NywiZXhwIjoyMDc2NjMxNTc3fQ.ijQ2sVBK4nOL3IiHhvrwmLuGJBxsmqzXwgQSrUe6jTs';
            }
            // For production environment, return the production service key
            if (_configOverride.url === 'https://killgsyghirjkdsjnfta.supabase.co') {
                return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtpbGxnc3lnaGlyamtkc2puZnRhIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc2MTA2MjA2NCwiZXhwIjoyMDc2NjM4MDY0fQ.Nj1_F_MmG9brvw2YdLZPEbo8qXcxjNgYhB8X-ENndJs';
            }
            // Fallback to anon key for unknown environments
            return _configOverride.anonKey;
        }
    }
    catch (error) {
        // Ignore configuration errors when used from service contexts
    }
    // Production fallback when called from service context (browser production deployment)
    if (typeof window !== 'undefined') {
        // We're in browser context, use production service key as fallback
        return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtpbGxnc3lnaGlyamtkc2puZnRhIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc2MTA2MjA2NCwiZXhwIjoyMDc2NjM4MDY0fQ.Nj1_F_MmG9brvw2YdLZPEbo8qXcxjNgYhB8X-ENndJs';
    }
    // If we can't determine the environment, throw error to make it obvious
    throw new Error('Unable to determine service key for environment - no valid configuration found');
};
// SINGLETON: Get or create the single Supabase client instance
export const getSupabaseClient = () => {
    // SINGLETON: Return existing client if available
    if (_supabaseClient) {
        console.log('✅ Reusing existing Supabase client singleton');
        return _supabaseClient;
    }
    console.log('🔄 No existing client, creating singleton...');
    const config = getSupabaseConfig();
    console.log('🔄 Creating new Supabase client with URL:', config.url);
    if (!config.url || !config.anonKey) {
        console.error('❌ Invalid Supabase config:', { url: !!config.url, anonKey: !!config.anonKey });
        throw new Error('Invalid Supabase configuration - missing URL or key');
    }
    try {
        _supabaseClient = createSupabaseClient();
        if (!_supabaseClient) {
            console.error('❌ createSupabaseClient returned null/undefined');
            throw new Error('Failed to create Supabase client - returned null');
        }
        if (!_supabaseClient.from) {
            console.error('❌ Created client missing from method - client:', !!_supabaseClient);
            throw new Error('Supabase client missing from method');
        }
        if (!_supabaseClient.auth) {
            console.error('❌ Created client missing auth method - client:', !!_supabaseClient);
            throw new Error('Supabase client missing auth method');
        }
        console.log(`✅ Supabase client singleton #${_clientCreationCount} created successfully with all methods`);
        return _supabaseClient;
    }
    catch (error) {
        console.error('❌ Failed to create Supabase client:', error);
        // Reset state on failure to allow retry
        _supabaseClient = null;
        throw error;
    }
};
// Export function that returns current client (called at runtime) 
export const getSupabase = () => {
    try {
        let client = getSupabaseClient();
        console.error('🔥 EMERGENCY DEBUG: getSupabase() called, client exists?', !!client);
        if (!client) {
            console.error('🚨 getSupabaseClient() returned null/undefined');
            console.error('🚨 _supabaseClient:', !!_supabaseClient);
            console.error('🚨 _configurationInitialized:', _configurationInitialized);
            console.error('🚨 _configOverride:', !!_configOverride);
            // Attempt to recreate client if configuration exists
            if (_configurationInitialized && _configOverride) {
                console.log('🔄 Attempting to recreate Supabase client...');
                _supabaseClient = null; // Force recreation
                client = getSupabaseClient();
                if (client) {
                    console.log('✅ Successfully recreated Supabase client');
                }
            }
            if (!client) {
                throw new Error('Supabase client is null and could not be recreated');
            }
        }
        // CRITICAL: Validate client has required methods
        if (client && (!client.from || !client.auth || !client.rpc)) {
            console.error('🔥 EMERGENCY: Client missing methods! from:', !!client.from, 'auth:', !!client.auth, 'rpc:', !!client.rpc);
            console.error('🔥 Client keys:', Object.keys(client));
            console.error('🔥 Client type:', typeof client);
            // Try recreating client completely
            try {
                console.log('🔄 Attempting emergency client recreation...');
                const config = getSupabaseConfig();
                const emergencyClient = createClient(config.url, config.anonKey, {
                    auth: {
                        autoRefreshToken: true,
                        persistSession: true,
                        detectSessionInUrl: true
                    }
                });
                console.log('🔥 Emergency client created, has methods? from:', !!emergencyClient.from, 'auth:', !!emergencyClient.auth);
                _supabaseClient = emergencyClient;
                return emergencyClient;
            }
            catch (emergencyError) {
                console.error('🔥 Emergency client creation failed:', emergencyError);
                throw emergencyError;
            }
        }
        return client;
    }
    catch (error) {
        console.error('🚨 Error in getSupabase():', error);
        throw error;
    }
};
// Legacy export for backward compatibility - fully lazy initialization
export const supabase = new Proxy({}, {
    get(target, prop) {
        try {
            // Always get the current client to ensure configuration updates are respected
            const client = getSupabaseClient();
            if (!client) {
                console.error('❌ Supabase client is null/undefined!');
                throw new Error('Supabase client not initialized');
            }
            const result = client[prop];
            if (typeof result === 'function') {
                return result.bind(client);
            }
            return result;
        }
        catch (error) {
            console.error('❌ Error accessing Supabase client property:', prop, error);
            throw error;
        }
    }
});
